/**
 * English translations
 */
import type { TranslationKeys } from '../types';

export const en: TranslationKeys = {
  common: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    pass: 'Pass',
    finish: 'Finish',
    close: 'Close',
    reset: 'Reset',
    waiting: 'Waiting for your turn...',
    loading: 'Loading...',
  },

  game: {
    titlePrefix: 'Age of Steam',
    subtitle: 'Solo Map',
    turn: 'Turn',
    phase: 'Phase',
    player: 'P1',
    bankrupt: 'BANKRUPT',
    gameOver: 'Game Over',
  },

  phases: {
    issueShares: 'Phase 1: Issue Shares',
    selectActions: 'Phase 3: Select Actions',
    buildTrack: 'Phase 4: Build Track',
    moveGoods: 'Phase 5: Move Goods',
    collectIncome: 'Phase 6: Collect Income',
    payExpenses: 'Phase 7: Pay Expenses',
    incomeReduction: 'Phase 8: Income Reduction',
    refillGoods: 'Phase 9: Refill Goods',
    advanceTurn: 'Phase 10: Advance Turn',
    completed: 'Game Completed',
  },

  stats: {
    cash: 'Cash',
    income: 'Income',
    expenses: 'Expenses',
    shares: 'Shares',
    locoLevel: 'Loco Lv.',
    cargoLevel: 'Cargo Lv.',
    goods: 'Goods',
    playerStatus: 'Player Status',
    // Mobile short labels
    cashShort: 'Cash:',
    incomeShort: 'Inc:',
    payShort: 'Pay:',
    issueShort: 'Issue:',
    locoShort: 'Loco:',
    cargoShort: 'Cargo:',
  },

  issueShares: {
    title: 'Issue Shares',
    numberOfShares: 'Number of shares to issue (Max: {max}):',
    cashReward: '+${amount} Cash',
    helpTitle: 'How special rules work:',
    barbados: {
      helpMaxPerTurn: 'Max 1 share per turn',
    },
    detroit: {
      helpMaxShares: 'Max 25 shares',
    },
  },

  selectActions: {
    title: 'Select Actions',
    actions: {
      engineer: {
        name: 'Engineer',
        description: 'Place 4 tiles instead of 3',
      },
      locomotive: {
        name: 'Locomotive',
        description: 'Free locomotive upgrade',
      },
      urbanization: {
        name: 'Urbanization',
        description: 'Urbanize a town',
      },
      production: {
        name: 'Production',
        description: 'Place 2 cubes on display',
      },
    },
    used: '(Used)',
    helpTitle: 'How special rules work:',
    helpSelectOne: 'Select 1 action per turn (required)',
    helpLocked: 'Used actions are locked until all 4 are used',
    helpReset: 'After 4 turns, all actions reset',
    insufficientFunds: 'Insufficient funds (need $5)',
    maxLevel: 'Already at max level',
    puertoRico: {
      costInfo: 'Action cost: $5',
      currentCash: 'Current Cash',
      skip: 'Skip (No Cost)',
      helpOptional: 'Action selection is optional',
      helpCost: 'Selecting an action costs $5',
      helpLocomotive: 'Locomotive action is applied immediately',
    },
    detroit: {
      costInfo: 'Fee based on disks remaining',
      currentCash: 'Current Cash',
      skip: 'Skip (No Cost)',
      helpOptional: 'Action selection is optional',
      helpCost: '3 disks=$3, 2 disks=$2, 1 disk=$1',
      helpLocomotive: 'Each action can be used up to 3 times',
    },
    seattle: {
      locomotiveDescription: 'Loco +1 or Cargo +1',
      skip: 'Skip',
      helpOptional: 'Select 0-2 actions per turn',
      helpCost: 'No immediate cost; $5/use at settlement',
      helpLocomotive: 'Locomotive: choose Loco +1 or Cargo +1',
      locoEffectTitle: 'Choose Locomotive Effect',
      locoUp: 'Locomotive +1',
      cargoUp: 'Cargo Transport +1',
      engineerUses: 'Engineer uses',
      locomotiveUses: 'Locomotive uses',
      settlementCost: 'Settlement cost',
    },
  },

  buildTrack: {
    title: 'Build Track',
    trackTiles: 'Track Tiles:',
    cityTiles: 'City:',
    remaining: 'remaining',
    townDisk: 'Town Disk:',
    cost: 'Cost: ${amount}',
    payment: 'Payment',
    overBudget: 'Over budget!',
    validation: {
      playerBankrupt: 'Bankrupt players cannot build track',
      invalidTileType: 'Invalid tile type: {type}',
      maxTilesReached: 'Maximum {max} tiles can be placed this turn',
      newCityLimitReached: 'Only 1 new city tile can be placed per turn',
      newCityNotAvailable: '{type} is not available in Barbados map (only A-D)',
      hexNotFound: 'Hex {hex} does not exist on the map',
      cannotBuildOnCity: 'Cannot place track on city hexes',
      cannotBuildOnUrbanizedTown: 'Cannot place track on urbanized town (treated as city)',
      townTileRequiresTownHex: 'Town tiles can only be placed on town hexes',
      newCityRequiresTownHex: 'New city tiles can only be placed on town hexes',
      urbanizationRequired: 'Urbanization action required to place new city tiles',
      urbanizationAlreadyUsed: 'Urbanization action has already been used this game',
      hexOccupied: 'Hex {hex} is already occupied',
      firstTrackMustConnectCity: 'First track must connect to a city',
      mustConnectToExisting: 'Track must be connected to your existing track or a city',
      floatingTrackPath: 'Track path {num} is not connected to any city or existing track',
      cannotConnectMapEdge: 'Tile cannot connect outside the map (direction: {dir})',
      cannotConnectSea: 'Tile cannot connect to sea (direction: {dir})',
      insufficientFunds: 'Insufficient funds: cost ${cost}, available ${available}',
      selfReferencingLink: 'This placement would form a link from {city} to itself',
      invalidReplacementPattern: 'Cannot replace double track with single track',
      invalidReplacementConnection: 'New tile must contain the existing track path',
      newPathNotConnected: 'New track path must connect to an adjacent tile or city',
      notAtUnfinishedSectionEnd: 'Single-to-single replacement is only allowed at the end of an unfinished section',
      cannotReplaceOtherPlayerTrack: "Cannot replace another player's track",
      replacementBreaksConnection: 'New tile must maintain connection to direction {edge}',
      cannotReplaceCompletedLink: 'Cannot replace tiles that are part of a completed link',
      cannotModifyOtherPlayerPath: "Cannot modify another player's track path in a double track replacement",
      pathNotAtUnfinishedSectionEnd: 'Modified path must be at the end of an unfinished section',
      townTileLimitReached: 'Town already has 2 non-town tiles placed (max: 2)',
      townDiskAlreadyPlaced: 'Town already has a non-town tile (max: 1 per town)',
      noTownDisksRemaining: 'No town disks remaining (max: 8 total)',
      noTrackToReplace: 'No track to replace at this hex',
      cannotBuildOnMountains: 'Cannot place track on mountain hexes in this map',
      wallBlocksConnection: 'Tile cannot connect through wall (direction: {dir})',
      townEdgeNotPreserved: 'Town edge {edge} not preserved in new tile',
      existingEdgeNotInTown: 'Existing edge {edge} not present in new Town tile',
      existingEdgeNotPreserved: 'Existing edge {edge} not preserved in new tile',
    },
    townDisksRemaining: 'Town Disks: {count}',
    help: {
      title: 'How to play',
      placeTile: 'Select a tile, then click a hex on the map. Each click rotates the tile. Press Confirm to place.',
      cancelTile: 'Press Cancel to undo placements made this turn.',
      finishBuild: 'Press Finish to pay costs and proceed to the next phase.',
      specialLink: 'Click the cost label on an adjacent link or ferry link to place it.',
    },
  },

  moveGoods: {
    title: 'Move Goods',
    selectGoods: 'Select goods to transport',
    guide: 'If multiple paths are available, each click switches the path.',
    transportable: 'Transportable ({count}):',
    noGoodsAvailable: 'No goods available',
    selectRoute: 'Select Route:',
    route: 'Route:',
    links: '{count} links',
    clickToChange: 'Click to change route',
    link: '{count} link',
    upgradeLoco: 'Upgrade Loco',
    maxLocoLevel: 'Max Loco Lv.',
    confirmUpgrade: 'Confirm Upgrade Loco?',
    validation: {
      invalidPhase: 'Current phase is {phase}, expected moveGoods',
      playerBankrupt: 'Bankrupt players cannot move goods',
      cityNotFound: 'City {city} not found',
      goodsNotFound: 'Goods {color} not found in city {city}',
      destinationColorMismatch: 'City {city} color is {actual}, expected {expected}',
      locoAlreadyUpgraded: 'Locomotive already upgraded this turn',
      maxLocoLevel: 'Locomotive level is already at maximum (6)',
    },
    seattle: {
      deliveriesRemaining: 'Deliveries remaining',
      selectGoods: 'Select goods to transport',
      grayCityNote: 'Gray City: choose to deliver (+1VP) or keep',
      grayCityTitle: 'Deliver to Gray City',
      grayCityPrompt: 'Choose what to do with the goods:',
      deliver: 'Deliver (Remove)',
      deliverDesc: 'Remove from game, +1VP at end',
      keep: 'Keep (Store)',
      keepDescAirport: 'Store at Airport',
      keepDescDowntown: 'Store at Downtown (unlimited)',
      airportFull: 'Airport is full (3/3)',
      specialRulesTitle: 'Special Rules',
      specialRule1: 'Ph.9: Airport auto-fills 1 cube (max 3). Roll 3 dice and place 3 cubes (max 2 per location). Demand overload = game over.',
      specialRule2: 'Downtown/Airport (white cities): Can temporarily store cubes for later transport.',
    },
  },

  refillGoods: {
    title: 'Refill Goods',
    productionSelected: 'Production Action Selected',
    drawnGoods: 'Drawn Goods:',
    openGoodsDisplay: 'Open Goods Display',
    rollingDice: 'Rolling dice...',
    rolled: 'Rolled: {dice1}, {dice2}',
    noGoodsPlaced: 'No goods placed',
    clickToProceed: 'Click Finish to proceed',
    seattle: {
      diceResults: 'Dice Results',
      airportAuto: 'Airport auto-placement',
      airportFull: 'Airport is full! Too many passengers are stranded.',
      demandOverload: 'Demand Overload!',
      selectGoods: 'Select goods to place',
      selectLocation: 'Select location',
      clickMapToPlace: 'Click a highlighted City/Town on the map',
      selectedLocation: 'Selected',
      noAvailableLocation: 'No available location - DEMAND OVERLOAD!',
      allPlaced: 'All goods placed!',
      proceed: 'Proceed',
      gameOver: 'Game Over',
      specialRulesTitle: 'Special Rules',
      specialRule1: 'Airport auto-fills 1 cube at start (max 3). If full, game over.',
      specialRule2: 'Roll 3 dice, place 3 cubes in corresponding areas (max 2 per location).',
      specialRule3: 'If any location reaches 3 cubes, immediate game over (Demand Overload).',
    },
  },

  autoPhase: {
    title: 'Auto Processing',
    processing: 'Processing...',
    clickToProceed: 'Click to proceed to next phase',
  },

  goodsDisplay: {
    title: 'Goods Display',
    production: 'Production',
    lightSide: 'Light Side',
    darkSide: 'Dark Side',
    legend: 'Legend',
    colors: {
      red: 'Red',
      blue: 'Blue',
      yellow: 'Yellow',
      black: 'Black',
      purple: 'Purple',
    },
    selectCube: 'Select cube',
    selectCell: 'Click empty cell',
    placed: '{count}/2 placed',
    selected: 'Selected',
  },

  // ============================================================================
  // Game Start Modal - 共通キー
  // ============================================================================
  gameStartCommon: {
    // ヘッダー
    title: 'Age of Steam',
    subtitle: 'Solo Map Rules',
    designedBy: 'Designed by',

    // 概要（マップ固有の description は各マップで定義）
    description: '',

    // 勝利/終了条件セクション
    victoryCondition: 'Victory Condition',
    gameEnd: 'Game ends after 10 turns',
    loseCondition: 'DEFEAT',

    // 特殊ルールセクション
    specialRules: 'Special Rules',
    phase: 'Ph.{num}',

    // 戦略ヒント
    strategyTips: 'Strategy Tips',
    tip1: '',
    tip2: '',
    tip3: '',
    tip4: '',

    // ボタン
    startGame: 'Start Game',
    backToMaps: 'Back to Maps',
    returnToGame: 'Return to Game',
  },

  // ============================================================================
  // Game Start Modal - マップ固有キー
  // ============================================================================
  gameStartBarbados: {
    // マップ固有の概要・条件
    description: 'The game is set on the Caribbean island of Barbados. Pineapples (yellow cubes) harvested from sugar plantations are transported to cities across the island, and the objective is to buy back your shares without going bankrupt by the end of the game.',
    gameEnd: 'Game ends after 10 turns',
    loseCondition: "If you can't buy back all shares, you lose!",
    tip1: 'Plan action selection carefully - each action is available once every 4 turns',
    tip2: 'Balance share issuance - you need cash but must buy them back at $5 each',
    tip3: 'Focus on income over track building - cash is your final score',
    tip4: 'Navigate the central mountains efficiently',

    // Phase ルール説明
    issueSharesRule: 'Issue Shares:',
    issueSharesDesc: 'Only 1 share per turn',
    selectActionsRule: 'Select Actions:',
    selectActionsDesc: '4 actions only -',
    selectActionsActions: 'Engineer, Locomotive, Urbanization, Production',
    refillGoodsRule: 'Goods Growth:',
    refillGoodsDesc: 'Roll 2 dice for light side only',

    // 勝利条件詳細
    buyBackShares: 'Buy back all shares at $5 each',
    remainingCash: 'Remaining cash is your final score',

    // 特殊ルール詳細
    selectActionsMarker: 'Each action gets a marker when selected.',
    selectActionsCannotReselect: 'Cannot reselect until all 4 have markers.',
    selectActionsReset: 'Then markers reset and all become available again.',
  },

  gameStartPuertoRico: {
    // マップ固有の概要・条件
    description: 'Transport red and black goods cubes to San Juan. Minimize black cube penalties.',
    gameEnd: 'Game ends after 10 turns',
    loseCondition: 'Income drops to 0 or below after penalty = DEFEAT',
    tip1: 'Prioritize transporting black cubes - they become -10 penalty each',
    tip2: 'Action selection is optional - save $5 when not needed',
    tip3: 'Build efficient routes to San Juan from all towns',
    tip4: 'Balance locomotive upgrades vs track building costs',

    // Phase ルール説明
    selectActionsRule: 'Select Actions:',
    selectActionsDesc: '2 actions only -',
    selectActionsActions: 'Engineer, Locomotive',
    refillGoodsRule: 'Goods Growth:',
    refillGoodsDesc: 'Skipped (no goods growth)',

    // 勝利条件詳細
    blackCubePenalty: '-10 income per remaining black cube',
    remainingScore: 'Final score = (Income × 3) + Links - (Shares × 3)',

    // 特殊ルール詳細
    selectActionsCost: 'Each action costs $5 to select',
    selectActionsOptional: 'Action selection is optional (can skip)',

    // 難易度選択
    difficultyLabel: 'Difficulty',
    difficultyDesc: 'Select difficulty (affects red/black cube ratio)',
    difficulties: {
      Beginner: 'Beginner',
      Novice: 'Novice',
      Expert: 'Expert',
      Master: 'Master',
      Conqueror: 'Conqueror',
      God: 'God',
    },
    cubeRatio: 'Red: {red} / Black: {black}',
    difficultyChangeNotice: '※ Difficulty changed. Game will restart on start.',
  },

  gameStartDetroit: {
    // マップ固有の概要・条件
    description: 'This map is modeled after Detroit in the mid-2000s. Instead of playing for a fixed number of turns, the game continues until a player goes bankrupt. Your goal is to defeat the virtual government, which goes bankrupt at the end of the sixth turn. If you survive longer than the virtual government, you win.',
    gameEnd: 'Game continues until bankruptcy',
    loseCondition: 'Bankrupt before turn 7 = DEFEAT',
    tip1: 'Start with $0 cash - issue shares immediately to fund actions',
    tip2: 'Each action costs $1-$3 - plan your economy carefully',
    tip3: 'Expenses grow each turn - build income quickly',
    tip4: 'Costly link building is covered by the engineer’s free tier  ',

    // Phase ルール説明
    issueSharesRule: 'Issue Shares:',
    issueSharesDesc: 'Up to 25 shares total (start with 5 issued, but start with $0)',
    selectActionsRule: 'Select Actions:',
    selectActionsDesc: '3 actions only -',
    selectActionsActions: 'Engineer, Locomotive, Urbanization',
    payExpensesRule: 'Pay Expenses:',
    payExpensesDesc: 'Expenses include turn number × $1 (increases each turn)',
    incomeReductionRule: 'Income Reduction:',
    incomeReductionDesc: '5-step reduction (faster than standard)',
    refillGoodsRule: 'Goods Growth:',
    refillGoodsDesc: 'Roll 2 dice for both sides',

    // 勝利条件詳細
    scoreIsTurns: 'Your score = number of turns survived',
    winCondition: 'Survive 7+ turns = VICTORY',

    // 特殊ルール詳細
    diskSystem: 'Each action has 3 disks (use limit: 3 times each)',
    diskFee: 'Fee: $3 (first use) → $2 → $1 (last use)',

    // 特殊リンク
    specialLinksTitle: 'Special Links/Hexes',
    ferryLinkDesc: 'Ferry Link: A virtual railway that directly connects distant hexes with a single link',
    adjustLincDesc: 'Adjust Link: A virtual railway that directly connects adjacent hexes with a single link',
  },

  gameStartSeattle: {
    // マップ固有の概要・条件
    description: 'In this map, goods cubes represent users of the Seattle transportation system. Your goal is to build a network that supports travel between cities while managing deferred action costs and handling a growing number of passengers without going bankrupt. The locomotive action can also be used to increase the number of goods transports.',
    gameEnd: 'Game ends after 10 turns',
    loseCondition: 'Final score below 0 OR settlement failed = DEFEAT',
    tip1: 'Balance Cargo Level upgrades vs maintenance costs',
    tip2: 'Use Downtown/Airport as temporary storage for difficult colors',
    tip3: 'Watch for demand overload warnings in Phase 5',
    tip4: 'Minimize action usage to reduce settlement costs',

    // Phase ルール説明
    selectActionsRule: 'Select Actions:',
    selectActionsDesc: '2 actions only (either one, both, or none) -',
    selectActionsActions: 'Engineer, Locomotive',
    locomotiveChoice: 'Locomotive: Choose Loco +1 OR Cargo Transport +1',
    actionTracking: 'Each use costs $5 at settlement',
    moveGoodsRule: 'Move Goods:',
    moveGoodsDesc: 'Deliver [Cargo Lv.] times (initial: 2, max: 5)',
    grayCities: 'Downtown/Airport: Any color OK, income +0',
    deliverOrKeep: 'Choose to deliver (score at the end) or keep for later transport',
    payExpensesDesc: 'Additional: Cargo Lv. maintenance (Lv.3=$4, Lv.4=$7, Lv.5=$10)',
    refillGoodsDesc: 'Airport auto-fill (1 per turn, up to a maximum of 3), roll 3 dice and place 3 cubes manually (up to a maximum of 2 per location)',
    demandOverload: '3rd cube at any location = immediate DEFEAT',

    // 勝利条件詳細
    winCondition: 'Final score 0 or above = VICTORY',
    settlementPayment: 'Settlement: $5 × shares + $5 × action uses',
    scoreCalculation: 'Score: Delivered to Downtown/Airport - Remaining cubes - Unconnected City/Town to Downtown',

    // 特殊リンク
    specialLinksTitle: 'Special Links/Hexes',
    preLaidTrack: 'Pre-laid Track: Downtown-Airport already connected (no owner)',
    wallsDesc: 'Red lines on map = walls (impassable)',
    mountainDesc: 'Cannot place tiles on mountain hexes',
  },

  gameEnd: {
    victory: 'VICTORY!',
    defeat: 'DEFEAT',
    bankruptcy: 'BANKRUPTCY',
    bankruptcyMessage: 'You ran out of money before the game ended. Try a more conservative strategy!',
    playAgain: 'Play Again',
    viewMap: 'View Map',
    viewScore: 'View Score',
    finalScore: 'Final Score',
    turnsPlayed: 'Turns Played',
    linksCompleted: 'Links Completed',
    sharesBuyback: 'Shares Buyback',
    // 統計ラベル
    statsTitle: 'Game Stats',
    turn: 'Turn',
    income: 'Income',
    links: 'Links',
    loco: 'Loco Lv.',
    // スコア計算ラベル
    cashLabel: 'Cash',
    buybackCost: 'Buyback',
    scoreLabel: 'Score',
    detroit: {
      evaluations: {
        turn12: 'You are in the Age of Steam Hall of Fame. Take a picture, date it, sign it, cherish it.',
        turn11: 'Eleven!',
        turn10: 'The cars are rolling off the factory line. Detroit Rock City salutes you.',
        turn9: 'You have advanced to expert level. The economy in Detroit is starting to turn around.',
        turn8: 'You have achieved above average skills and Detroit applauds your efforts.',
        turn7: 'You are better than the government, way to go!',
        defeat: 'The government outlasted you. Try again!',
      },
    },
    barbados: {
      evaluations: {
        cash31: 'Outstanding! You are a master of the Caribbean trade routes.',
        cash21: 'Excellent! The pineapple empire thrives under your management.',
        cash11: 'Great job! You have proven yourself a capable trader.',
        cash1: 'Well done! You managed to turn a profit.',
        cash0: 'Just survived! You bought back all shares with nothing to spare.',
        defeat: 'You could not buy back all shares. Better luck next time!',
      },
    },
    puertoRico: {
      evaluations: {
        score61: 'Legendary! You have conquered Puerto Rico.',
        score41: 'Excellent! The island prospers under your leadership.',
        score21: 'Great job! A solid performance.',
        score1: 'Well done! You managed to succeed.',
        score0: 'Just survived! You barely made it.',
        defeat: 'The black cubes overwhelmed you. Try again!',
      },
    },
    seattle: {
      demandOverloadTitle: 'Demand Overload',
      demandOverload: 'Transit demand exceeded capacity!',
      settlementFailedTitle: 'Settlement Failed',
      settlementFailed: 'Settlement Failed!',
      settlement: 'Settlement',
      shares: 'Shares',
      actions: 'Actions',
      total: 'Total',
      cash: 'Cash',
      score: 'Score Calculation',
      scoreFormula: 'Score = Delivered − Remaining − Unconnected',
      delivered: 'Delivered cubes (Downtown/Airport)',
      remaining: 'Remaining cubes on map',
      unconnected: 'Unconnected cities/towns',
      finalScore: 'Final Score',
      evaluations: {
        excellent: 'Outstanding! Seattle\'s transit system is world-class!',
        good: 'Great job! The city is well connected.',
        passing: 'You made it! Seattle has basic transit coverage.',
        poor: 'The transit system needs improvement...',
        veryPoor: 'Seattle deserves better transit leadership.',
      },
    },
  },

  settings: {
    title: 'Settings',
    language: 'Language',
    languageEnglish: 'English',
    languageJapanese: '日本語',
    close: 'Close',
    // 追加機能
    showRules: 'Show Rules',
    resetGame: 'Quit Game',
    resetGameConfirm: 'Return to map selection?',
    undoToTurnStart: 'Undo to Turn Start',
    undoToTurnStartConfirm: 'Undo all actions this turn?',
  },

  mapSelection: {
    title: 'Age of Steam',
    subtitle: 'Select a map to play',
    comingSoon: 'Coming Soon',
    players: 'Players:',
    solo: 'Solo',
    selectDifficulty: 'Select Difficulty',
    redCubes: 'Red:',
    blackCubes: 'Black:',
    cancel: 'Cancel',
    play: 'Play',
    mapEditor: 'Map Editor',
    deviceRecommendation: 'PC recommended. On smartphones, please play in landscape mode.',
    hallOfHonor: 'Hall of Honor',
    achievements: 'Achievements',
    statistics: 'Statistics',
    difficulty: 'Difficulty:',
    difficultyStars: {
      easy: '★☆☆',
      medium: '★★☆',
      hard: '★★★',
    },
    maps: {
      barbados: {
        name: 'Barbados',
        description: 'Master action selection timing. Perfect introduction to Age of Steam.',
      },
      puertoRico: {
        name: 'Puerto Rico',
        description: 'Test your luck with adjustable difficulty.',
      },
      detroit: {
        name: 'Detroit Bankruptcy',
        description: 'Cash is tight! See how many turns you can survive before bankruptcy.',
      },
      seattle: {
        name: 'Seattle Metro',
        description: 'Avoid bankruptcy and demand overload while building transport capacity.',
      },
    },
  },

  hallOfHonor: {
    title: 'Hall of Honor',
    back: 'Back',
    periodAll: 'All Time',
    periodWeekly: 'Weekly',
    periodMonthly: 'Monthly',
    rank: '#',
    player: 'Player',
    score: 'Score',
    turn: 'Turn',
    date: 'Date',
    watch: 'Watch',
    noScores: 'No scores registered yet.',
    prev: 'Prev',
    next: 'Next',
    scoresRegistered: '{count} score(s) registered',
    loading: 'Loading...',
    error: 'Failed to load rankings',
    rank1: '1st',
    rank2: '2nd',
    rank3: '3rd',
    cash: 'Cash',
    shares: 'Shares',
    locoLv: 'Loco',
    links: 'Links',
    cargoLv: 'Cargo',
    delivered: 'Delivered',
    remaining: 'Remaining',
    unconnected: 'Unconn.',
    undoCount: 'Undo',
    filterAll: 'All',
    filterNoUndo: 'No Undo',
  },

  achievements: {
    title: 'Achievements',
    back: 'Back',
    cookieNotice: 'Achievement data is stored in your browser\'s local storage. Clearing site data will reset your progress.',
    unlocked: 'Unlocked',
    locked: 'Locked',
    progress: '{unlocked} / {total} unlocked',
    names: {
      // Barbados
      barbados_cash60: 'Caribbean Trade King',
      barbados_cash40: 'Pineapple Empire',
      barbados_cash20: 'Skilled Merchant',
      barbados_first_win: 'First Victory',
      barbados_shares6: 'Cautious Trader',
      barbados_shares4: 'Debt-Free Mogul',
      barbados_no_undo: 'Perfect Play',
      // Detroit
      detroit_turn25: 'Eternal Survivor',
      detroit_turn20: 'Indomitable',
      detroit_turn15: 'The President',
      detroit_turn12: 'Hall of Fame',
      detroit_turn10: 'Rock City',
      detroit_turn8: 'Above Average',
      detroit_no_undo: 'No Second Chances',
      // Puerto Rico
      puerto_rico_score70: 'Legendary Governor',
      puerto_rico_score50: 'Island Prosperity',
      puerto_rico_score30: 'Solid Governance',
      puerto_rico_first_win: 'First Victory',
      puerto_rico_clean_island: 'Island Purifier',
      puerto_rico_cubes3: 'Efficient Delivery',
      puerto_rico_shares13: 'Frugal Governor',
      puerto_rico_shares10: 'Austere Ruler',
      puerto_rico_no_undo: 'Seasoned Voyage',
      // Seattle
      seattle_score20: 'Legendary Planner',
      seattle_score15: 'Future City',
      seattle_score10: 'World Class',
      seattle_score5: 'Average Network',
      seattle_score0: 'Just Made It',
      seattle_all_connected: 'Full Coverage',
      seattle_loco5: 'Economy Engine',
      seattle_loco3: 'Steam Master',
      seattle_cargo4: 'Efficient Logistics',
      seattle_cargo3: 'Minimal Freight',
      seattle_shares10: 'Frugal Planner',
      seattle_shares7: 'Ultra Frugal',
      seattle_no_undo: 'Perfect Urban Plan',
    },
    descriptions: {
      // Barbados
      barbados_cash60: 'Clear with a final score of 60 or more',
      barbados_cash40: 'Clear with a final score of 40 or more',
      barbados_cash20: 'Clear with a final score of 20 or more',
      barbados_first_win: 'Clear without going bankrupt',
      barbados_shares6: 'Clear with 6 or fewer shares issued',
      barbados_shares4: 'Clear with 4 or fewer shares issued',
      barbados_no_undo: 'Clear without using undo',
      // Detroit
      detroit_turn25: 'Survive for 25 turns',
      detroit_turn20: 'Survive for 20 turns',
      detroit_turn15: 'Survive for 15 turns',
      detroit_turn12: 'Survive for 12 turns',
      detroit_turn10: 'Survive for 10 or more turns',
      detroit_turn8: 'Survive for 8 or more turns',
      detroit_no_undo: 'Win without using undo',
      // Puerto Rico
      puerto_rico_score70: 'Clear with a final score of 70 or more',
      puerto_rico_score50: 'Clear with a final score of 50 or more',
      puerto_rico_score30: 'Clear with a final score of 30 or more',
      puerto_rico_first_win: 'Survive the black cube menace and win',
      puerto_rico_clean_island: 'Clear with all black cubes delivered',
      puerto_rico_cubes3: 'Clear with 3 or fewer cubes remaining',
      puerto_rico_shares13: 'Clear with 13 or fewer shares issued',
      puerto_rico_shares10: 'Clear with 10 or fewer shares issued',
      puerto_rico_no_undo: 'Clear without using undo',
      // Seattle
      seattle_score20: 'Clear with a final score of 20 or more',
      seattle_score15: 'Clear with a final score of 15 or more',
      seattle_score10: 'Clear with a final score of 10 or more',
      seattle_score5: 'Clear with a final score of 5 or more',
      seattle_score0: 'Clear with a score of 0 or more (victory)',
      seattle_all_connected: 'Clear with all stations connected',
      seattle_loco5: 'Clear with locomotive level 5 or less',
      seattle_loco3: 'Clear with locomotive level 3 or less',
      seattle_cargo4: 'Clear with cargo transport level 4 or less',
      seattle_cargo3: 'Clear with cargo transport level 3 or less',
      seattle_shares10: 'Clear with 10 or fewer shares issued',
      seattle_shares7: 'Clear with 7 or fewer shares issued',
      seattle_no_undo: 'Clear without using undo',
    },
  },

  statistics: {
    title: 'Statistics',
    back: 'Back',
    storageNotice: 'Statistics are stored in your browser\'s local storage. Clearing site data will reset your stats.',
    playCount: 'Games Played',
    clearCount: 'Games Won',
    noUndoClearCount: 'No-Undo Wins',
    bestScore: 'Best Score',
    avgCash: 'Avg. Cash',
    avgShares: 'Avg. Shares',
    avgLoco: 'Avg. Loco Lv',
    avgLinks: 'Avg. Links',
    avgCargoLv: 'Avg. Cargo Lv',
    avgDelivered: 'Avg. Delivered',
    avgRemaining: 'Avg. Remaining',
    avgUnconnected: 'Avg. Unconnected',
    noData: 'No data yet. Play a game to see your stats!',
  },
};
